import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface SearchOptionsListProps<OptionType extends CascaderOption>
  extends BoxProps {
  listRef?: React.RefObject<HTMLUListElement>;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalSearchOptionsList = <OptionType extends CascaderOption>({
  children,
  listRef,
  ...props
}: {
  /** The children to be rendered. */
  children: React.ReactNode;
  listRef?: React.RefObject<HTMLUListElement>;
}) => {
  const { comboboxApi, cascaderStyles, getCommonProps } =
    useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSearchOptionsList = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SEARCH_OPTIONS_LIST),
    [getComponent],
  );

  return (
    <CustomSearchOptionsList
      {...props}
      listRef={listRef}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.searchOptionsList}
      {...comboboxApi.getContentProps()}
      className="cascader-search-options-list"
    >
      {children}
    </CustomSearchOptionsList>
  );
};

export const SearchOptionsList = <OptionType extends CascaderOption>({
  children,
  getCommonProps: _,
  listRef,
  ...rest
}: SearchOptionsListProps<OptionType>) => {
  return (
    <Box as="ul" {...rest} ref={listRef}>
      {children}
    </Box>
  );
};
